import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Floating legend for the Gangland Map. Shows only the marker categories
 * actually present in the current dataset so it doesn't shout options that
 * don't apply. Collapsible on mobile so it never eats the map on a phone.
 *
 * `categoriesMeta` - object mapping normalized key → { emoji, color, label }
 * `visibleKeys`    - normalized category keys we want to render (excludes 'All')
 */
const MapLegend = ({ categoriesMeta, visibleKeys }) => {
  const [open, setOpen] = useState(true);

  const entries = (visibleKeys || [])
    .filter((k) => k && k !== 'All' && categoriesMeta[k])
    .map((k) => ({ key: k, ...categoriesMeta[k] }));

  if (entries.length === 0) return null;

  return (
    <div
      data-testid="map-legend"
      className="absolute bottom-3 left-3 z-[400] max-w-[240px] rounded-lg border border-red-500/40 bg-black/85 backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="map-legend-toggle"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={open}
      >
        <span className="text-red-400 text-[10px] font-black uppercase tracking-[0.25em]">
          Legend · {entries.length}
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5 text-zinc-400" />
        )}
      </button>
      {open && (
        <ul
          className="px-2 pb-2 pt-0 space-y-1 max-h-56 overflow-y-auto"
          data-testid="map-legend-list"
        >
          {entries.map((e) => (
            <li
              key={e.key}
              data-testid={`map-legend-item-${e.key}`}
              className="flex items-center gap-2 px-2 py-1 rounded text-[11px]"
            >
              <span
                aria-hidden
                className="inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0"
                style={{ backgroundColor: `${e.color}22`, border: `1px solid ${e.color}66` }}
              >
                <span style={{ fontSize: 12, lineHeight: 1 }}>{e.emoji}</span>
              </span>
              <span className="font-black tracking-wide text-zinc-200">
                {e.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MapLegend;
